import apiClient from "../../api/api";

/**
 * Creates a new Job record.
 *
 * @async
 * @function requestCreateJob
 *
 * @param {Object} job - Job payload object.
 * @param {string} job.accountProjectType - Selected project type identifier.
 * @param {number} job.projectId - Selected project name.
 * @param {string} job.jobTitle - Job title.
 * @param {string} job.jobDescription - Job description.
 * @param {string} job.department - Department name.
 * @param {string} job.location - Job location.
 * @param {string} job.employmentType - Employment type (e.g., Full-time, Contract).
 * @param {number} job.offerRate - Offered rate or salary.
 * @param {number} job.requiredCandidates - Number of required candidates.
 * @param {string} job.overview - Job overview text.
 * @param {string} job.responsibilities - Job responsibilities description.
 * @param {string} job.requirements - Required qualifications.
 * @param {string} job.nonNegotiables - Mandatory requirements.
 * @param {string} job.niceToHaves - Desired qualifications.
 * @param {string} job.benefits - Benefits description.
 * @param {boolean} job.clientContractSigned - Indicates if client contract is signed.
 * @param {string} job.status - Job status (default: "NOT_STARTED").
 * @param {Object} job.catalog - Catalog information.
 * @param {Array<number>} job.catalog.SKILL - Selected skill identifiers.
 * @param {Array<number>} job.catalog.LANGUAGE - Selected language identifiers.
 * @param {string} job.assessments - Assessments string (comma-separated or formatted string).
 *
 * @returns {Promise<Object>} Returns created job response data.
 *
 * @throws Will throw an error if the API request fails.
 */
export const requestCreateJob = async(job) => {
    const createJobResponse = await apiClient.post(import.meta.env.VITE_BASE_JOBS_URL, job, {successToast:"Job request created"})
    return createJobResponse.data
}

/**
 * Fetch all jobs available in the manager dashboard.
 *
 * @async
 * @function requestGetJobs
 * @returns {Promise<Object[]>} A promise that resolves with the list of jobs.
 * @throws {Error} Throws an error if the request fails.
 */
export const requestGetJobs = async() => {
    const params = {
        page: 1,
        limit: 100
    }
    const getJobsResponse = await apiClient.get(import.meta.env.VITE_BASE_JOBS_URL, { params })
    return getJobsResponse.data
}

/**
 * Fetch jobs that are publicly available on the jobs board.
 *
 * @async
 * @function requestGetBoardJobs
 * @returns {Promise<Object[]>} A promise that resolves with the list of board jobs.
 * @throws {Error} Throws an error if the request fails.
 */
export const requestGetBoardJobs = async (page = 1, limit = 100, uuid = 0, applicantId = 0) => {
    let url = `${import.meta.env.VITE_JOBS_BOARD_URL}?page=${page}&limit=${limit}`;

    if (uuid !== 0) {
        url += `&uuid=${uuid}`;
    }

    if (applicantId !== 0) {
        url += `&applicantId=${applicantId}`;
    }

    const response = await apiClient.get(url);

    return response.data;
};

/**
 * Fetch detailed information about a specific job.
 *
 * @async
 * @function requestGetJob
 * @param {string} uuid - Unique identifier of the job.
 * @returns {Promise<Object>} A promise that resolves with the job details.
 * @throws {Error} Throws an error if the request fails.
 */
export const requestGetJob = async(uuid) => {
    const response = await apiClient.get(import.meta.env.VITE_BASE_JOBS_URL + `/${uuid}`)
    return response.data
}

/**
 * Update the status of a job offer.
 *
 * @async
 * @function requestUpdateJobStatus
 * @param {string} jobUuid - Unique identifier of the job to update.
 * @param {string} status - New status for the job (e.g., NOT_STARTED, OPEN, CLOSED).
 * @returns {Promise<void>} Resolves when the status update request completes.
 * @throws {Error} Throws an error if the request fails.
 */
export const requestUpdateJobStatus = async(jobUuid, status) => {
    const payload = {
        status
    }
    await apiClient.patch(import.meta.env.VITE_BASE_JOBS_URL+`/${jobUuid}`, payload, {successToast: `Job offer status updated to ${status}`})
}

/**
 * Update a job offer with new data.
 *
 * @async
 * @function requestUpdateJobs
 * @param {Object} updatedJob - The job object containing updated fields.
 * @param {string} updatedJob.uuid - Unique identifier of the job.
 * @returns {Promise<void>} Resolves when the job update request completes.
 * @throws {Error} Throws an error if the request fails.
 */
export const requestUpdateJobs = async(uuid, updatedJob) => {
    const response = await apiClient.patch(import.meta.env.VITE_BASE_JOBS_URL+`/${uuid}`, updatedJob, {successToast: "Job offer updated successfully"})

    return response.data
}

/**
 * Fetch dashboard metrics related to job offers.
 *
 * @async
 * @function requestGetJobsDashboard
 * @returns {Promise<Object>} A promise that resolves with job dashboard metrics.
 * @throws {Error} Throws an error if the request fails.
 */
export const requestGetJobsDashboard = async() => {
    const response = await apiClient.get(import.meta.env.VITE_JOBS_DASHBOARD_URL)

    return response.data
}


export const requestjobPipeline = async(jobUuid) => {
    const response = await apiClient.patch(`${import.meta.env.VITE_BASE_JOBS_URL}/${jobUuid}/open-pipeline`)

    return response.data    
}